import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import { isPromoActive, promoConfig } from "../data/promoConfig";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const courseTypes = [
  "Rock",
  "Valse",
  "Mariage",
  "Rock acrobatique",
  "Danse de salon",
  "Cours collectif",
  "Autre",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    course_type: "Rock",
    location: "",
    message: "",
    promo_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const promoFieldRef = useRef(null);
  const promoActive = isPromoActive();

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // Pre-fill promo code from session (set by PromoBanner CTA) or custom event
  useEffect(() => {
    const intent = sessionStorage.getItem("lm_promo_intent");
    if (intent) {
      setForm((f) => ({ ...f, promo_code: intent }));
      sessionStorage.removeItem("lm_promo_intent");
      setTimeout(() => {
        promoFieldRef.current?.focus();
        promoFieldRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 600);
    }
    const handler = (e) => {
      const code = e.detail?.code;
      if (!code) return;
      setForm((f) => ({ ...f, promo_code: code }));
      setTimeout(() => {
        promoFieldRef.current?.focus();
      }, 800);
    };
    window.addEventListener("lm:promo-prefill", handler);
    return () => window.removeEventListener("lm:promo-prefill", handler);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir nom, email et message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Demande envoyée !");
      setDone(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        course_type: "Rock",
        location: "",
        message: "",
        promo_code: "",
      });
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de l'envoi. Réessayez plus tard.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-[#0E1A2B]">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#C9A869]" />
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#C9A869]">Contact</span>
          </div>
          <h2 className="serif font-light text-4xl md:text-5xl lg:text-6xl text-[#F7F3EA] tracking-tight leading-[1.05]">
            Réservons votre <span className="italic gold-text">premier cours</span>.
          </h2>
          <p className="mt-7 text-base text-[#F7F3EA]/75 font-light leading-relaxed max-w-md">
            Une question, un projet, un mariage à préparer ? Écrivez-moi quelques mots, je vous réponds personnellement sous 6h en moyenne.
          </p>
          <div className="mt-10 space-y-4 text-sm">
            <Info label="Email" value="louismelchiorgiraud@icloud.com" />
            <Info label="Zone" value="France & Luxembourg" />
            <Info label="Déplacement" value="Partout (frais en sus selon zone)" />
          </div>

          {promoActive && (
            <div
              data-testid="contact-promo-callout"
              className="mt-10 border border-[#C9A869] bg-gradient-to-br from-[#C9A869]/15 to-[#C47A66]/10 p-6"
            >
              <div className="flex items-center gap-3 mb-2">
                <Gift size={18} className="text-[#C9A869]" />
                <p className="text-[11px] tracking-[0.3em] uppercase text-[#C9A869] font-semibold">
                  {promoConfig.title}
                </p>
              </div>
              <p className="serif text-xl text-[#F7F3EA] font-light leading-snug">
                Votre 1<sup>er</sup> cours est offert avec le code{" "}
                <span className="font-mono font-semibold bg-[#0E1A2B] text-[#C9A869] px-2 py-0.5 not-italic">
                  {promoConfig.code}
                </span>
              </p>
              <p className="text-xs text-[#F7F3EA]/65 mt-2 font-light">
                Une utilisation par client. Mentionnez le code dans votre message ou dans le champ
                dédié du formulaire.
              </p>
            </div>
          )}
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="bg-[#1A2A40]/40 border border-[#C9A869]/30 p-8 md:p-10 space-y-5"
        >
          {done && (
            <div data-testid="contact-success" className="border border-[#C9A869] bg-[#C9A869]/10 p-4 text-sm text-[#C9A869]">
              Votre demande a bien été envoyée. À très vite !
            </div>
          )}

          <Field label="Nom" name="name" value={form.name} onChange={onChange} required />
          <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
          <Field label="Téléphone (facultatif)" name="phone" value={form.phone} onChange={onChange} />

          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#C9A869] mb-2">
              Type de cours
            </label>
            <select
              name="course_type"
              value={form.course_type}
              onChange={onChange}
              data-testid="contact-course-type"
              className="w-full bg-transparent border border-[#C9A869]/30 text-[#F7F3EA] py-3 px-4 focus:border-[#C9A869] outline-none transition-colors"
            >
              {courseTypes.map((c) => (
                <option key={c} value={c} className="bg-[#0E1A2B] text-[#F7F3EA]">
                  {c}
                </option>
              ))}
            </select>
          </div>

          <Field label="Lieu (ville)" name="location" value={form.location} onChange={onChange} />

          {/* Promo code field — visible always; highlighted when promo active */}
          <div>
            <label className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#C9A869] mb-2">
              <Gift size={12} />
              Code promo {promoActive && <span className="text-[#C47A66] normal-case tracking-normal">(actif jusqu'au {new Date(promoConfig.endDate).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })})</span>}
            </label>
            <input
              ref={promoFieldRef}
              type="text"
              name="promo_code"
              value={form.promo_code}
              onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })}
              placeholder={promoActive ? `Ex: ${promoConfig.code}` : "Optionnel"}
              data-testid="contact-field-promo_code"
              className={`w-full bg-transparent border text-[#F7F3EA] py-3 px-4 outline-none transition-colors font-light font-mono tracking-[0.15em] ${
                form.promo_code
                  ? "border-[#C9A869] bg-[#C9A869]/5"
                  : "border-[#C9A869]/30 focus:border-[#C9A869]"
              }`}
            />
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#C9A869] mb-2">
              Message
            </label>
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              data-testid="contact-message"
              rows={5}
              required
              className="w-full bg-transparent border border-[#C9A869]/30 text-[#F7F3EA] py-3 px-4 focus:border-[#C9A869] outline-none transition-colors font-light"
              placeholder="Parlez-moi de votre projet, votre niveau, vos disponibilités…"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="w-full px-8 py-4 bg-[#C9A869] text-[#0E1A2B] text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-[#DCC28E] disabled:opacity-60 transition-colors duration-500"
          >
            {loading ? "Envoi en cours…" : "Envoyer ma demande"}
          </button>

          <p className="text-[11px] text-[#F7F3EA]/55 leading-relaxed font-light pt-3 border-t border-[#C9A869]/15">
            Les informations envoyées via ce formulaire sont utilisées uniquement pour répondre à
            votre demande de cours. Vous pouvez demander l'accès, la modification ou la suppression
            de vos données à tout moment en écrivant à{" "}
            <a
              href="mailto:louismelchiorgiraud@icloud.com"
              className="text-[#C9A869] hover:text-[#DCC28E] underline underline-offset-2"
            >
              louismelchiorgiraud@icloud.com
            </a>
            . Pour en savoir plus, consultez notre{" "}
            <a
              href="/confidentialite"
              className="text-[#C9A869] hover:text-[#DCC28E] underline underline-offset-2"
            >
              politique de confidentialité
            </a>
            .
          </p>
        </form>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required }) => (
  <div>
    <label className="block text-[10px] tracking-[0.3em] uppercase text-[#C9A869] mb-2">
      {label}{required && " *"}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={`contact-field-${name}`}
      className="w-full bg-transparent border border-[#C9A869]/30 text-[#F7F3EA] py-3 px-4 focus:border-[#C9A869] outline-none transition-colors font-light"
    />
  </div>
);

const Info = ({ label, value }) => (
  <div className="flex items-baseline gap-4">
    <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A869] w-28">{label}</span>
    <span className="text-[#F7F3EA]/85">{value}</span>
  </div>
);
